"use client"

// components/tracker/drug-card.tsx
//
// One tracked drug on the Drugs overview (FE-DRUG-PAGE-1 Phase 4a-ii). Two
// variants from PZH-0: the resolved card (Q31-0, spec §2: orb + tracking
// pill, name block, three stats, phase bar with legend, mapped-indication
// chips, footer) and the fallback card for a name with no canonical record
// (Q3I-0, spec §3, with the plan's copy decisions). The phase bar carries the
// QLW-0 hover / keyboard-focus tooltip (spec §5.1).
//
// The sphere comes from `useDrugAvatar(name)` so the card and the quick view
// paint the same data URI for one drug, and every orb carries
// `data-identity` (asset | name) as the identity rule requires. There is no
// `<img>`: the URI is a background so the drawer can measure the same string.
//
// Honesty copy. The card never says "mechanism". A null field on a resolved
// card means that feed's read FAILED and reads "Not loaded"; a genuine zero
// reads "0". The fallback card had no reads at all (there is no asset to
// read), so its unbacked stats read "No record" rather than "Not loaded",
// and its trial count is the WATCH's own attribution, labelled as such.

import { useId, useState } from "react"

import { useDrugAvatar } from "@/components/tracker/drug-identity-context"
import type {
  DrugCard as DrugCardData,
  DrugCardPhaseBucket,
} from "@/lib/drugs/load-drugs-overview"

import styles from "@/components/tracker/drugs-page.module.css"

/** The five segments in bar order, with their QKY-0 fills and legend text. */
export const PHASE_SEGMENTS: ReadonlyArray<{
  key: DrugCardPhaseBucket
  label: string
  tipLabel: string
  fill: string
  className: string
  /** The two light fills need ink text on the tooltip, or it would not read. */
  darkText: boolean
}> = [
  { key: "P1", label: "P1", tipLabel: "Phase 1", fill: "#C3C8CE", className: styles.phaseSegP1, darkText: true },
  { key: "P2", label: "P2", tipLabel: "Phase 2", fill: "#9AA0A6", className: styles.phaseSegP2, darkText: false },
  { key: "P3", label: "P3", tipLabel: "Phase 3", fill: "#6B7280", className: styles.phaseSegP3, darkText: false },
  { key: "P4", label: "P4", tipLabel: "Phase 4", fill: "#3C4043", className: styles.phaseSegP4, darkText: false },
  { key: "Other", label: "Other", tipLabel: "Other / unlisted", fill: "#E4E8EC", className: styles.phaseSegOther, darkText: true },
]

/** Up to three chips, as Q31-0 draws. */
const CHIPS_ON_CARD = 3

const num = (n: number) => n.toLocaleString("en-GB")

/**
 * One alias as the card, the row and the drawer print it (4a-ii review fix
 * 2, display casing). A `brand`-typed alias that is entirely lower case
 * ("invokana", from `openfda_ndc_brand`) gets its first letter capitalised.
 * Codes, INNs, registry names and anything with a capital already are left
 * exactly as stored. THIS IS A RENDER PROJECTION: the stored `asset_aliases`
 * row is unchanged, and the search still matches the stored spelling.
 */
export function displayAliasText(a: { alias: string; type: string | null }): string {
  const alias = a.alias
  if ((a.type ?? "").trim().toLowerCase() !== "brand") return alias
  if (alias !== alias.toLowerCase()) return alias
  return alias.charAt(0).toUpperCase() + alias.slice(1)
}

/**
 * The alias line under the name, shared by the card and the compact row.
 * Null aliases mean the read failed; an empty list means nothing survived
 * the ranking and the display filter (plan copy: "No brand or code on
 * record"); a fallback card has no record to read.
 */
export function aliasLineText(card: DrugCardData): string {
  if (card.assetId === null) return "No canonical record"
  if (card.aliases === null) return "Names not loaded"
  if (card.aliases.length === 0) return "No brand or code on record"
  return card.aliases.map(displayAliasText).join(" · ")
}

/** Share of the total to one decimal, as QLW-0 shows ("33.9%"). */
function share(count: number, total: number): string {
  if (total <= 0) return "0.0"
  return ((count / total) * 100).toFixed(1)
}

/**
 * QKU-0 with the QLW-0 contract: each of the five segments is hoverable and
 * keyboard-focusable and reveals a tooltip, tinted with its own fill, of
 * `<phase>` and `<count> trials · <share>%`. The tooltip is a sibling of the
 * bar (the bar clips its overflow) positioned at the segment's centre; the
 * active segment points at it through `aria-describedby`.
 */
export function PhaseBar({
  counts,
}: {
  counts: NonNullable<DrugCardData["phaseCounts"]>
}) {
  const [active, setActive] = useState<DrugCardPhaseBucket | null>(null)
  const tipId = useId()
  const total = counts.total

  // Segment centres as a share of the bar, for the tooltip's `left`. The
  // 3px gaps shift this by a pixel or two; the arrow still lands on the
  // segment it names.
  let cursor = 0
  const centres: Partial<Record<DrugCardPhaseBucket, number>> = {}
  for (const seg of PHASE_SEGMENTS) {
    const c = counts.byPhase[seg.key] ?? 0
    if (c > 0 && total > 0) {
      centres[seg.key] = ((cursor + c / 2) / total) * 100
      cursor += c
    }
  }

  const activeSeg = active ? PHASE_SEGMENTS.find((s) => s.key === active) : null
  const activeCount = active ? (counts.byPhase[active] ?? 0) : 0

  return (
    <div className={styles.phaseFrame}>
      <div className={styles.phaseBar} role="group" aria-label="Trial phases">
        {PHASE_SEGMENTS.map((seg) => {
          const c = counts.byPhase[seg.key] ?? 0
          if (c <= 0) return null
          const isActive = active === seg.key
          return (
            <button
              key={seg.key}
              type="button"
              className={`${styles.phaseSeg} ${seg.className}`}
              style={{ "--seg-count": c } as React.CSSProperties}
              aria-label={`${seg.tipLabel}: ${num(c)} ${c === 1 ? "trial" : "trials"}, ${share(c, total)}%`}
              aria-describedby={isActive ? tipId : undefined}
              data-phase={seg.key}
              onMouseEnter={() => setActive(seg.key)}
              onMouseLeave={() => setActive((v) => (v === seg.key ? null : v))}
              onFocus={() => setActive(seg.key)}
              onBlur={() => setActive((v) => (v === seg.key ? null : v))}
            />
          )
        })}
      </div>
      {activeSeg && active ? (
        <div
          id={tipId}
          role="tooltip"
          data-phase-tip={active}
          className={`${styles.phaseTip} ${activeSeg.darkText ? styles.phaseTipDarkText : ""}`}
          style={{ left: `${centres[active] ?? 50}%`, background: activeSeg.fill }}
        >
          <span>{activeSeg.tipLabel}</span>
          <span>
            {num(activeCount)} {activeCount === 1 ? "trial" : "trials"} · {share(activeCount, total)}%
          </span>
          <svg
            className={styles.phaseTipArrow}
            viewBox="0 0 10 6"
            aria-hidden="true"
          >
            <path d="M0 0H10L5 6Z" fill={activeSeg.fill} />
          </svg>
        </div>
      ) : null}
    </div>
  )
}

/** QL4-0: the five swatches and labels. Only under a bar with data. */
function Legend() {
  return (
    <ul className={styles.legend} aria-hidden="true">
      {PHASE_SEGMENTS.map((seg) => (
        <li key={seg.key} className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: seg.fill }} />
          {seg.label}
        </li>
      ))}
    </ul>
  )
}

function Stat({
  value,
  label,
  none,
}: {
  value: number | null
  label: string
  /** What a null reads as: "Not loaded" (a failed read) or "No record". */
  none: string
}) {
  return (
    <div className={styles.stat}>
      {value === null ? (
        <p className={styles.statValueNone}>{none}</p>
      ) : (
        <p className={styles.statValue}>{num(value)}</p>
      )}
      <p className={styles.statLabel}>{label}</p>
    </div>
  )
}

export function DrugCard({
  card,
  selected = false,
  onOpen,
}: {
  card: DrugCardData
  /** The card behind an open quick view (Q83-0 border, nothing more). */
  selected?: boolean
  /**
   * Opens the quick view; absent on a fallback card. The opener element is
   * passed from `e.currentTarget` so focus can return to it on close
   * (Safari does not focus a clicked button, so `document.activeElement`
   * is not a reliable record of it).
   */
  onOpen: (card: DrugCardData, opener: HTMLElement | null) => void
}) {
  const avatar = useDrugAvatar(card.name)
  /* A11Y: the id of the fallback card's visible "Find a match" note. */
  const matchNoteId = useId()
  const resolved = card.assetId !== null

  const aliasLine = aliasLineText(card)

  return (
    <article
      className={`${styles.card} ${selected ? styles.cardSelected : ""}`}
      data-asset-id={card.assetId ?? undefined}
      data-resolved={resolved ? "true" : "false"}
      aria-label={card.name}
    >
      {/* Block 1: orb, name and the tracking pill. */}
      <div className={styles.cardTop}>
        <span
          className={styles.orb}
          style={{ backgroundImage: `url("${avatar.src}")` }}
          data-identity={avatar.identity}
          role="img"
          aria-label={`${card.name} sphere`}
        />
        <h2 className={styles.name}>{card.name}</h2>
        <span className={styles.trackingPill}>✓ Tracking</span>
      </div>

      {/* Block 2: alias line and class line (the class line only when an axis
          has a value; the fallback card draws none). */}
      <div className={styles.nameBlock}>
        <p className={styles.aliasLine} title={aliasLine}>
          {aliasLine}
        </p>
        {resolved && card.classLine ? (
          <p className={styles.classLine} title={card.classLine}>
            {card.classLine}
          </p>
        ) : null}
      </div>

      {/* Block 3: the three stats. On the fallback card the trial count is
          the watch's own attribution, and the other two were never read. */}
      <div className={styles.stats}>
        <Stat
          value={card.trialCount}
          label={resolved ? "Linked trials" : "Watched trials"}
          none={resolved ? "Not loaded" : "No record"}
        />
        <Stat
          value={card.licenceCount}
          label="Licence matches"
          none={resolved ? "Not loaded" : "No record"}
        />
        <Stat
          value={card.acceptedNameCount}
          label="Accepted names"
          none={resolved ? "Not loaded" : "No record"}
        />
      </div>

      {/* Block 4: the phase bar. An empty bar (a failed read, or no record)
          carries no legend (plan decision). */}
      <div className={styles.phaseBlock}>
        <p className={styles.phaseHead}>
          <span>Trial phases</span>
          <span>
            {card.phaseCounts
              ? `${num(card.phaseCounts.total)} linked ${card.phaseCounts.total === 1 ? "trial" : "trials"}`
              : resolved
                ? "Not loaded"
                : "No record"}
          </span>
        </p>
        {card.phaseCounts && card.phaseCounts.total > 0 ? (
          <>
            <PhaseBar counts={card.phaseCounts} />
            <Legend />
          </>
        ) : (
          <div className={styles.phaseBarEmpty} aria-hidden="true" />
        )}
      </div>

      {/* Block 5: mapped indications. Chips carry the full MeSH name, never
          an abbreviation. "View indications" opens the quick view (its
          "Licensed uses" block) until the record page lands in 4b. */}
      <div className={styles.indicationsBlock}>
        <p className={styles.blockHeading}>Mapped indications</p>
        <div className={styles.chipRow}>
          {!resolved ? (
            <span className={styles.chipEmpty}>No indication data</span>
          ) : card.approvedIndications === null ? (
            <span className={styles.chipEmpty}>Indication data not loaded</span>
          ) : card.approvedIndications.length === 0 ? (
            <span className={styles.chipEmpty}>No licensed indication on record</span>
          ) : (
            card.approvedIndications.slice(0, CHIPS_ON_CARD).map((ind) => (
              <span key={ind.meshId} className={styles.chip} title={ind.name}>
                {ind.name}
              </span>
            ))
          )}
        </div>
        {resolved ? (
          <button
            type="button"
            className={styles.indicationsLink}
            onClick={(e) => onOpen(card, e.currentTarget)}
          >
            View indications →
          </button>
        ) : null}
      </div>

      {/* Block 6: footer. The caption is the provenance of the chips above
          (name-matched licences); the fallback card names what it is. */}
      <div className={styles.cardFoot}>
        <p className={styles.footCaption}>
          {resolved ? "Licence links matched by name" : "Name only, no canonical record"}
        </p>
        {resolved ? (
          <button
            type="button"
            className={styles.footAction}
            onClick={(e) => onOpen(card, e.currentTarget)}
            aria-haspopup="dialog"
          >
            Open drug ↗
          </button>
        ) : (
          /* "Find a match" (plan decision) has no destination yet: matching
             a watch name to a record lands with the explore search surface.
             Drawn, inert, and honest about it. */
          <span
            className={styles.footAction}
            aria-disabled="true"
            aria-describedby={matchNoteId}
          >
            Find a match
          </span>
        )}
      </div>
      {/* A11Y: the visible reason "Find a match" is inert, not a
          tooltip-only title. */}
      {resolved ? null : (
        <p id={matchNoteId} className={styles.footNote}>
          Matching a name to a record is not available yet. It lands with explore search.
        </p>
      )}
    </article>
  )
}

/**
 * The compact-list row: the same fields as the card in a 56px row (an
 * undrawn state built from tracker patterns; plan decision).
 */
export function DrugRow({
  card,
  selected = false,
  onOpen,
}: {
  card: DrugCardData
  selected?: boolean
  onOpen: (card: DrugCardData, opener: HTMLElement | null) => void
}) {
  const avatar = useDrugAvatar(card.name)
  const resolved = card.assetId !== null
  const aliasLine = aliasLineText(card)

  const statText = (value: number | null, one: string, many: string) =>
    value === null
      ? resolved
        ? "Not loaded"
        : "No record"
      : `${num(value)} ${value === 1 ? one : many}`

  return (
    <div
      className={styles.row}
      data-asset-id={card.assetId ?? undefined}
      data-resolved={resolved ? "true" : "false"}
      data-selected={selected ? "true" : undefined}
    >
      <span
        className={styles.rowOrb}
        style={{ backgroundImage: `url("${avatar.src}")` }}
        data-identity={avatar.identity}
        role="img"
        aria-label={`${card.name} sphere`}
      />
      <div className={styles.rowMain}>
        <p className={styles.rowName}>{card.name}</p>
        <p className={styles.rowSub} title={aliasLine}>
          {aliasLine}
        </p>
      </div>
      <span className={styles.rowClass} title={card.classLine ?? undefined}>
        {resolved && card.classLine ? card.classLine : ""}
      </span>
      <span
        className={`${styles.rowStat} ${card.trialCount === null ? styles.rowStatNone : ""}`}
      >
        {statText(card.trialCount, resolved ? "linked trial" : "watched trial", resolved ? "linked trials" : "watched trials")}
      </span>
      <span
        className={`${styles.rowStat} ${card.acceptedNameCount === null ? styles.rowStatNone : ""}`}
      >
        {statText(card.acceptedNameCount, "accepted name", "accepted names")}
      </span>
      {resolved ? (
        <button
          type="button"
          className={styles.footAction}
          onClick={(e) => onOpen(card, e.currentTarget)}
          aria-haspopup="dialog"
        >
          Open drug ↗
        </button>
      ) : (
        <span className={styles.footAction} aria-disabled="true">
          Find a match
        </span>
      )}
    </div>
  )
}
